import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { RoutingData } from '@core/routing-data';
import { LanguageService } from '@services/language.service';
import { RoutingService } from '@services/routing.service';
import { Language } from '@models/language.model';
import { Globals } from '@core/globals';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private meta = inject(Meta);
  private title = inject(Title);
  private languageService = inject(LanguageService);
  private routingService = inject(RoutingService);
  private globals = inject(Globals);

  setTitle(title: string): void {
    const language: Language = this.languageService.language();
    this.title.setTitle(title + ' | ' + RoutingData.webTitle[language]);
  }

  setCanonical(url?: string): void {
    if (url?.length) {
      let canonicalUrl = url.split('?')[0];
      canonicalUrl = url.split('#')[0];
      const canonicalTag = this.meta.getTag('rel="canonical"');
      if (canonicalTag) this.meta.removeTag('rel="canonical"');
      this.meta.addTag({ rel: 'canonical', href: canonicalUrl });
    } else {
      const canonicalTag = this.meta.getTag('rel="canonical"');
      if (canonicalTag) this.meta.removeTag('rel="canonical"');
    }
  }

  setRobotsIndex(): void {
    const activePage = this.routingService.getActivePage(this.globals.language());
    if (activePage.id === 'urlShortener') {
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    } else {
      this.meta.removeTag('name="robots"');
    }
  }

  getDescription(): string {
    const language: Language = this.languageService.language();
    const activePage = this.routingService.getActivePage(language);
    const pageId: string = activePage.id;
    let description: string = 'custom';
    const defaultDescription: string = RoutingData.defaultDescription[language];
    const page = RoutingData.pages.find((page) => page.id === pageId);
    if (page) {
      if (activePage.numberOfParams < 1) {
        description = page.description?.[language] || defaultDescription;
      } else {
        description = page.paramsDescription?.[language] || page.description?.[language] || defaultDescription;
      }
    } else {
      description = defaultDescription;
    }
    return description;
  }

  setDescription(description?: string): void {
    description = description ?? this.getDescription();
    if (description === 'custom') return;
    this.meta.updateTag({ name: 'description', content: description });
  }

  removeOgData(): void {
    this.meta.removeTag('property="og:url"');
    this.meta.removeTag('property="og:type"');
    this.meta.removeTag('property="og:title"');
    this.meta.removeTag('property="og:description"');
    this.meta.removeTag('property="og:image"');
  }

  setOgData(data: { url?: string; type?: string; siteName?: string; title?: string; description?: string; image?: string } = {}): void {
    data.type = data.type || 'website';
    data.siteName = data.siteName || 'Mototechna';
    data.image = data.image || '/favicon/og-image.png';
    const language: Language = this.languageService.language();
    const ogData: { property: string; content: string }[] = [];
    if (data.url) ogData.push({ property: 'og:url', content: data.url });
    if (data.type) ogData.push({ property: 'og:type', content: data.type });
    if (data.siteName) ogData.push({ property: 'og:site_name', content: data.siteName });
    if (data.title) ogData.push({ property: 'og:title', content: data.title + ' | ' + RoutingData.webTitle[language] });
    if (data.description) ogData.push({ property: 'og:description', content: data.description });
    if (data.image) ogData.push({ property: 'og:image', content: data.image });
    this.removeOgData();
    this.meta.addTags(ogData);
  }
}
